<template>
  <div class="preview-content-container">
    <!-- Header: Profile and Name -->
    <v-row class="mt-4" justify="center" no-gutters>
      <v-col cols="12" align="center">
        <v-avatar size="170" class="profile-avatar mb-4">
          <v-img :src="profile || '/images/default-profile.png'"></v-img>
        </v-avatar>
        <h3 class="doctor-name mb-6" t-data="info-title">{{ name }}</h3>
      </v-col>
    </v-row>

    <!-- Info Sections -->
    <div class="info-sections">
      <!-- Department -->
      <v-row no-gutters class="mb-5 align-start">
        <v-col cols="auto" class="mr-4">
          <v-avatar color="primary100Color" size="28">
            <v-img src="/images/icons/department-icon.png" aspect-ratio="1" max-height="24" max-width="24" contain t-data="department-icon"></v-img>
          </v-avatar>
        </v-col>
        <v-col>
          <div class="info-label mb-1 body-regular" t-data="department-title">{{ lang == 'th' ? 'แผนก' : 'Department' }}</div>
          <div class="info-values department-list">
            <h6 v-for="(dept, index) in departments" :key="index" class="department-item" t-data="department-name">
              {{ dept }}
            </h6>
          </div>
        </v-col>
      </v-row>

      <!-- Specialization -->
      <v-row no-gutters class="mb-5 align-start">
        <v-col cols="auto" class="mr-4">
          <v-avatar color="primary100Color" size="28">
            <v-img src="/images/icons/specialty-icon.png" aspect-ratio="1" max-height="24" max-width="24" contain></v-img>
          </v-avatar>
        </v-col>
        <v-col>
          <div class="info-label mb-1 body-regular">{{ lang == 'th' ? 'ความชำนาญพิเศษ' : 'Specialty' }}</div>
          <div class="info-values d-flex flex-wrap">
            <v-chip
              v-for="(spec, index) in specialties"
              :key="index"
              class="mr-2 mb-2 custom-chip body-bold"
              outlined
            >
              {{ spec }}
            </v-chip>
          </div>
        </v-col>
      </v-row>

      <!-- Languages -->
      <v-row no-gutters class="mb-5 align-start">
        <v-col cols="auto" class="mr-4">
          <v-avatar color="primary100Color" size="28">
            <v-img src="/images/icons/lang-icon.png" aspect-ratio="1" max-height="24" max-width="24" contain></v-img>
          </v-avatar>
        </v-col>
        <v-col>
          <div class="info-label mb-1 body-regular">{{ lang == 'th' ? 'ภาษาที่ใช้สื่อสาร' : 'Languages' }}</div>
          <div class="info-values d-flex flex-wrap">
            <v-chip
              v-for="(l, index) in languages"
              :key="index"
              class="mr-2 mb-2 custom-chip body-bold"
              outlined
            >
              {{ formatLanguage(l, lang) }}
            </v-chip>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Education & Experience (Description) -->
    <div class="description-section mt-2">
      <div
        class="content-detail body-regular"
        t-data="description"
        v-html="description"
      ></div>
    </div>

    <v-divider class="my-6"></v-divider>

    <!-- Department Selector and Schedule -->
    <div class="schedule-section">
      <v-row class="mb-6">
        <v-col cols="12">
          <v-select
            v-model="internalSelectedDeptId"
            :items="departmentOptions"
            label="Department"
            outlined
            dense
            hide-details
            class="custom-disabled-select"
            @change="$emit('change-department', $event)"
            disabled
          ></v-select>
        </v-col>
      </v-row>

      <h4 class="schedule-headline mb-4 body-bold">{{ lang == 'th' ? 'วันและเวลาออกตรวจ' : "Doctor's Schedule" }}</h4>
      <div class="schedule-scroll-container mb-6">
        <div class="schedule-grid">
          <v-row v-for="(day, dayIdx) in daysOfWeek" :key="dayIdx" no-gutters class="schedule-row button-bold">
            <v-col class="day-label-col" :class="{'weekend': dayIdx >= 5}">
              <div class="day-label">{{ day.label }}</div>
            </v-col>
            <v-col class="slots-col py-2 px-3">
              <div class="d-flex flex-nowrap slot-scroller">
                <template v-if="getSchedulesForDay(dayIdx + 1).length > 0">
                  <div
                    v-for="(slot, slotIdx) in getSchedulesForDay(dayIdx + 1)"
                    :key="slotIdx"
                    class="schedule-card mr-3"
                  >
                    <div class="slot-time d-flex align-center button-bold">
                      {{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}
                    </div>
                    <div v-if="slot.remark" class="slot-remark d-flex align-center mt-1">
                      <v-icon x-small class="mr-1" color="secondaryTextColor">mdi-information</v-icon>
                      <span class="remark-text" :title="slot.remark">{{ slot.remark }}</span>
                    </div>
                  </div>
                </template>
                <div v-else class="no-slot"></div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>

      <div v-if="contentRepeaters && contentRepeaters.length > 0">
        <div class="d-flex justify-space-between v-expansion-panel-header"
                v-for="(item, index) in contentRepeaters"
                :key="index">
          <div>
            <h6 class="mb-0 card-title body-regular primaryTextColor--text">
              {{ item.title }}
            </h6>
          </div>
          <div class="v-expansion-panel-header__icon">
            <i aria-hidden="true" class="v-icon notranslate mdi mdi-chevron-down theme--light">
            </i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PreviewContent',
  props: {
    lang: {type: String},
    profile: {type: String},
    name: {type: String},
    description: {type: String},
    departments: {type: Array, default: () => []},
    specialties: {type: Array, default: () => []},
    languages: {type: Array, default: () => []},
    schedules: {type: Array, default: () => []},
    departmentOptions: {type: Array, default: () => []},
    selectedDeptId: {type: [String, Number]},
    contentRepeaters: {type: Array, default: () => []},
  },
  data() {
    return {
      internalSelectedDeptId: this.selectedDeptId,
    }
  },
  watch: {
    selectedDeptId(val) {
      this.internalSelectedDeptId = val
    }
  },
  computed: {
    daysOfWeek() {
      return this.lang == 'th' ? [
        {label: 'จ'},
        {label: 'อ'},
        {label: 'พ'},
        {label: 'พฤ'},
        {label: 'ศ'},
        {label: 'ส'},
        {label: 'อา'},
      ]: [
        {label: 'Mon'},
        {label: 'Tue'},
        {label: 'Wed'},
        {label: 'Thu'},
        {label: 'Fri'},
        {label: 'Sat'},
        {label: 'Sun'},
      ]
    }
  },
  methods: {
    getSchedulesForDay(dayNum) {
      // Assuming schedules is an array where each day indexed 0-6 or 1-7
      // Based on doctor.js SET_SCHEDULES_LIST, it's an array of 7 arrays (daysOfWeek)
      if (!this.schedules || this.schedules.length < dayNum) return []
      return this.schedules[dayNum - 1].filter(s => !s.is_final && s.start_time && s.end_time)
    },
    formatTime(timeStr) {
      if (!timeStr) return ''
      return timeStr.substring(0, 5) // HH:mm
    },
    formatLanguage(val, lang) {
      if (!val) return ''
      if (val.includes('-')) {
        const parts = val.split('-')
        if (lang === 'th') {
          return parts[1] ? parts[1].trim() : parts[0].trim()
        } else {
          return parts[0].trim()
        }
      }
      return val
    }
  }
}
</script>

<style lang="scss">
.preview-content-container {
  font-family: 'SukhumvitTadmai', sans-serif;
}

.profile-avatar {
  border: 1px solid var(--v-borderColor-base);
}

.doctor-name {
  color: var(--v-headerTextColor-base);
  font-weight: 700;
  font-size: 22px;
}

.info-label {
  color: var(--secondary-text-color);
  font-weight: 500;
}

.info-values {
  color: var(--primary-500-color);
  font-weight: 700;
  font-size: 16px;
}

.v-chip.v-chip--outlined.v-chip.v-chip.custom-chip {
  border: 1px solid var(--primary-500-color) !important;
  border-radius: 14px;
  padding: 4px 12px;
  background: var(--info-200-color) !important;
  background-color: var(--info-200-color) !important;
  color: var(--primary-500-color) !important;
  font-weight: 700;
  height: 28px;
}

.content-detail {
  ::v-deep {
    p {
      margin-bottom: 8px;
    }
    ul {
      padding-left: 20px;
      margin-bottom: 12px;
    }
    li {
      margin-bottom: 4px;
    }
  }
}

.schedule-scroll-container {
  overflow-x: auto;
  border: 1px solid var(--info-200-color);
  border-radius: 8px;
}

.schedule-grid {
  min-width: fit-content;
}

.schedule-row {
  border-bottom: 1px solid var(--info-200-color);
  min-height: 48px;
  display: flex;
  flex-wrap: nowrap;
  &:last-child {
    border-bottom: none;
  }
}

.day-label-col {
  background-color: var(--info-200-color);
  display: flex;
  align-items: center;
  border-right: 1px solid var(--info-200-color);
  padding-left: 8px !important;
  min-height: 74px !important;
  flex: 0 0 60px;
}

.day-label {
  color: #2E5696;
  font-weight: 700;
  font-size: 14px;
}

.slots-col {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
}

.schedule-card {
  background-color: var(--warning-300-color);
  border-radius: 6px;
  padding: 6px 10px;
  min-width: 130px;
  max-width: 200px;
  min-height: 69px !important;
  flex: 0 0 auto;
}

.slot-time {
  font-weight: 700;
  font-size: 14px;
  color: var(--primary-text-color);
}

.slot-remark {
  font-size: 12px;
  color: var(--secondary-text-color);
  min-width: 0;
  font-weight: 400;
}

.remark-text {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.department-select {
  ::v-deep {
    .v-input__slot {
      min-height: 40px !important;
    }
  }
}

.schedule-headline {
  color: var(--typography-primary-text-color);
  font-weight: 700;
}

.custom-expansion-panel.v-expansion-panel::before {
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  -webkit-transition: none !important;
  transition: none !important;
}

.theme--light.v-select.custom-disabled-select .v-select__selection--disabled {
  color: rgba(0,0,0,0.87);
}
</style>
